/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import debounce from 'lodash.debounce';
import isNull from 'lodash.isnull';
import {translate} from '@ion/core/src/locale/locale';
import {
  AssetBulkEditorFormFields
} from '@ion/core/src/types/editors';
import {
  Asset,
  Entity
} from '@ion/core/src/types/entities';
import {dateFormatter, timeDiff, toISOString} from '@ion/core/src/utils/DateUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {AssetFormFields, getADMAssetTypes} from '@ion/dls/src/editors/AssetEditor';
import {getInitialState} from '@ion/dls/src/editors/BulkDeviceEditorUtils';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import styles from '@ion/dls/src/editors/editorStyles';
import EntityStatusChooser from '@ion/dls/src/editors/EntityStatusChooser';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {GroupChooser} from '@ion/dls/src/editors/GroupChooser';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {useHasChange} from '@ion/dls/src/hooks/useHasChange';
import {useMergeState} from '@ion/dls/src/hooks/useMergeState';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {convertStatus} from '@ion/dls/src/utils/ComponentUtils';
import {
  useCurrentGlobalGroup
} from '../hooks/useCurrentGlobalGroup';
import {
  useSanitizeAccount
} from '../hooks/useSanitizeAccount';

interface AssetsBulkEditFormProps {
  onSave: (details: BulkAssetEditDetails) => void;
}

interface BulkAssetEditDetails {
  asset: AssetFormFields;
}

interface TargetType {
  name: AssetFormFields;
  value: any;
  required?: boolean;
}

const useStyles = (theme: DLSTheme) => ({
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    marginLeft : '17px'
  }
});

/**
 * Re-usable form with the details for Bulk Edit of a assets.
 */
export const AssetsBulkEditForm = forwardRef(({
  onSave
}: AssetsBulkEditFormProps, ref) => {
  const editorStyles                          = useExtendStyles(styles, useStyles);
  const classes                               = editorStyles();
  const [formState, setFormState]             = useFormState(getInitialState({}, []));
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const hasChange                             = useHasChange(formState);
  const globalGroup                           = useCurrentGlobalGroup();
  const account                               = useSanitizeAccount(globalGroup);
  const [isValidDates, setIsValidDates]       = useState<boolean>(true);
  const installDate                           = formState?.installDate?.value || null;
  const uninstallDate                         = formState?.uninstallDate?.value || null;

  useEffect(() => {
    if (!isNull(installDate) && !isNull(uninstallDate)) {
      const daysDiff = timeDiff(dateFormatter(uninstallDate), dateFormatter(installDate));
      const isValid  = daysDiff >= 0;
      setIsValidDates(isValid);
    }
  }, [installDate, uninstallDate]);

  const onValidForm = (data) => {
    if (hasChange && isValidDates) {
      onSave({asset: data});
    }
  };

  const onSubmit = useFormSave({
    onSave : onValidForm,
    serialize,
    formState,
    setFormState
  });

  const debounceFieldChange = debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    setFormState({[name]: {name, value, required}});
  }, 400);

  const handleFieldChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.persist();
    debounceFieldChange(e.target);
  }, []);

  const handleChooserChange = useCallback(({name, value, required = false}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
  }, []);

  const handleDateChange = useCallback(({name, value, required = false}) => {
    setFormState({[name]: {name, value: toISOString(value), required}});
  }, []);

  const onGroupChooserChange = useCallback(value => {
    handleChooserChange({
      name : AssetBulkEditorFormFields.Groups,
      value
    });
  }, []);

  const onAssetTypeChange = useCallback(({target:{value}}) => {
    handleChooserChange({
      name : AssetBulkEditorFormFields.AssetType,
      value
    });
  }, []);

  const onEntityStatusChooserChange = useCallback(value => {
    handleChooserChange({
      name : AssetBulkEditorFormFields.Status,
      value
    });
  }, []);

  const onInstallDateChange = useCallback((value, required) => {
    handleDateChange({
      name : AssetBulkEditorFormFields.InstallDate,
      value,
      required
    });
  }, []);

  const onUnInstallDateChange = useCallback((value, required) => {
    handleDateChange({
      name : AssetBulkEditorFormFields.UninstallDate,
      value,
      required
    });
  }, []);

  const onToggleInstallDatePicker = useCallback(() => {
    const {InstallDate} = AssetBulkEditorFormFields;
    setOpenDatePickers({
      [InstallDate] : !openDatePickers[InstallDate]
    });
  }, [openDatePickers]);

  const onToggleUnInstallDatePicker = useCallback(() => {
    const {UninstallDate} = AssetBulkEditorFormFields;
    setOpenDatePickers({
      [UninstallDate] : !openDatePickers[UninstallDate]
    });
  }, [openDatePickers]);

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  return (
    <EmbeddedForm>
      <FormSection bottomBorder
        title={translate('admin-assets:form.sections.general')}>
        <>
          <FormItem>
            <GroupChooser account={account}
              onChange={onGroupChooserChange}
              hasError={formState[AssetBulkEditorFormFields.Groups]?.error}
              defaultValue={formState[AssetBulkEditorFormFields.Groups]?.value} />
          </FormItem>
          <FormItem>
            <Dropdown displaySelectedLabel
              options={getADMAssetTypes()}
              freeSolo
              fieldLabel={translate('common:editors.entityType.label', {name: translate('common:entityLabels.asset')})}
              placeholder={translate('common:options.none')}
              value={formState[AssetBulkEditorFormFields.AssetType]?.value}
              name={AssetBulkEditorFormFields.AssetType}
              autoSelect
              onChange={onAssetTypeChange} />
          </FormItem>
          <FormItem>
            <FormTextField fieldLabel={translate('common:editors.entityCategory.label', {name: translate('common:entityLabels.asset')})}
              placeholder={translate('common:editors.entityCategory.placeholder', {name: translate('common:entityLabels.asset')})}
              name={AssetBulkEditorFormFields.AssetCategory}
              onChange={handleFieldChange}
              hasError={formState[AssetBulkEditorFormFields.AssetCategory]?.error}
              defaultValue={formState[AssetBulkEditorFormFields.AssetCategory]?.value}
              maxLength={64} />
          </FormItem>
          <FormItem>
            <EntityStatusChooser entity={Entity.asset}
              onChange={onEntityStatusChooserChange}
              status={formState[AssetBulkEditorFormFields.Status]?.value} />
          </FormItem>
          <FormItem className={classes.datePickerItem}>
            <DatePicker closeOnClickOutside
              useDatePickerTextField
              fieldLabel={translate('common:editors.installDate.label')}
              placeholder={translate('common:editors.shared.selectDate')}
              open={openDatePickers[AssetBulkEditorFormFields.InstallDate]}
              toggle={onToggleInstallDatePicker}
              onChange={onInstallDateChange}
              hasError={!isValidDates}
              defaultValue={formState[AssetBulkEditorFormFields.InstallDate]?.value} />
          </FormItem>
          <FormItem className={classes.datePickerItem}>
            <DatePicker closeOnClickOutside
              useDatePickerTextField
              fieldLabel={translate('common:editors.uninstallDate.label')}
              placeholder={translate('common:editors.shared.selectDate')}
              open={openDatePickers[AssetBulkEditorFormFields.UninstallDate]}
              toggle={onToggleUnInstallDatePicker}
              onChange={onUnInstallDateChange}
              hasError={!isValidDates}
              defaultValue={formState[AssetBulkEditorFormFields.UninstallDate]?.value} />
          </FormItem>
          {!isValidDates && (
            <span className={classes.errorMessage}>{translate('admin-assets:invalidDateErrorMsg')}</span>
          )}
        </>
      </FormSection>
      <FormSection title={translate('admin-assets:form.sections.details')}>
        <>
          <FormItem>
            <FormTextField fieldLabel={translate('common:editors.make.label')}
              name={AssetBulkEditorFormFields.Make}
              onChange={handleFieldChange}
              hasError={formState[AssetBulkEditorFormFields.Make]?.error}
              defaultValue={formState[AssetBulkEditorFormFields.Make]?.value} />
          </FormItem>
          <FormItem>
            <FormTextField fieldLabel={translate('common:editors.model.label')}
              name={AssetBulkEditorFormFields.Model}
              onChange={handleFieldChange}
              hasError={formState[AssetBulkEditorFormFields.Model]?.error}
              defaultValue={formState[AssetBulkEditorFormFields.Model]?.value} />
          </FormItem>
          <FormItem>
            <FormTextField fieldLabel={translate('common:editors.year.label')}
              name={AssetBulkEditorFormFields.Year}
              onChange={handleFieldChange}
              hasError={formState[AssetBulkEditorFormFields.Year]?.error}
              defaultValue={formState[AssetBulkEditorFormFields.Year]?.value} />
          </FormItem>
        </>
      </FormSection>
    </EmbeddedForm>
  );
});

const serialize = (data: FormState) => {
  const assetData: Asset = {};
  for (const [key, obj] of Object.entries(data)) {
    switch (key) {
      case AssetFormFields.Groups :
        assetData.account = obj.value;
        break;
      case AssetFormFields.AssetType :
        assetData[key] = obj.value?.value || obj.value || '';
        break;
      case AssetFormFields.Status :
        assetData.status = obj.value ? convertStatus(obj.value) : obj.value;
        break;
      default :
        if (!obj.value && obj.value !== 0) break;
        assetData[key] = obj.value ?? null;
    }
  }

  return assetData;
};

export default memo(AssetsBulkEditForm);
